import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, startWith } from 'rxjs/operators';

import { AutomationCall, AutomationCallMetrics, AutomationCallService } from 'src/app/shared/services/automation-call.service';
import { BroadcastCall, BroadcastCallMetrics, BroadcastCallService } from 'src/app/shared/services/broadcast-call.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';

/**
 * As abas da tela: as duas origens de ligação da plataforma e o consolidado.
 *
 * `all` não é uma terceira origem — é a SOMA das outras duas (ver `load()`).
 */
export type CallOrigin = 'all' | 'broadcast' | 'automation';

/**
 * O que os cards mostram, independente da origem. As cinco faixas de desfecho
 * são idênticas nas duas abas (mesma classificação no backend), então a única
 * coisa que muda é o "objeto" contado — broadcasts numa, automações na outra.
 */
interface CallMetricsView {
  total_calls: number;
  total_refused: number;
  total_answered_hangup: number;
  total_answered_dtmf: number;
  total_answered_listened: number;
}

@Component({
  selector: 'app-ligacoes-metricas',
  templateUrl: './ligacoes-metricas.component.html',
  styleUrls: ['./ligacoes-metricas.component.scss'],
})
export class LigacoesMetricasComponent implements OnInit, OnDestroy {

  loading = false;

  /** Assinaturas de vida longa (os autocompletes, carregados uma vez). */
  subscriptions: Subscription[] = [];

  /**
   * O request de métricas em voo. Fica fora de `subscriptions` porque é
   * substituído a cada troca de aba/filtro/período — acumular no array vazaria
   * uma assinatura por clique e deixaria respostas obsoletas chegando.
   */
  private metricsSub?: Subscription;

  /** A primeira emissão de `queryParams` sempre carrega, mesmo na aba padrão. */
  private initialTabApplied = false;

  /**
   * Teto do período em dias — o mesmo `CallMetricsService::MAX_WINDOW_DAYS` do
   * backend. Validar aqui evita mandar um request que só pode voltar 422 e
   * troca o toast genérico por um que diz o que ajustar.
   */
  static readonly MAX_WINDOW_DAYS = 366;

  /**
   * Aba ativa. Vive na query string (`?tab=`) pelo mesmo motivo de
   * `/#/calls-report`: dá pra mandar o link da aba certa para alguém, e um F5
   * não joga o usuário de volta pra aba padrão.
   */
  activeTab: CallOrigin = 'all';

  dateFilter: 'today' | '7d' | '30d' | 'custom' = '30d';
  customStartDate: Date | null = null;
  customEndDate: Date | null = null;

  // Filtro por broadcast com autocomplete
  broadcasts: BroadcastCall[] = [];
  selectedBroadcastId = '';
  broadcastSearchCtrl = new FormControl('');
  filteredBroadcasts$: Observable<BroadcastCall[]> = of([]);

  // Filtro por automação (campanha de /#/campaigns) com autocomplete
  automations: AutomationCall[] = [];
  selectedAutomationId = '';
  automationSearchCtrl = new FormControl('');
  filteredAutomations$: Observable<AutomationCall[]> = of([]);

  broadcastMetrics: BroadcastCallMetrics = LigacoesMetricasComponent.emptyBroadcast();
  automationMetrics: AutomationCallMetrics = LigacoesMetricasComponent.emptyAutomation();

  /**
   * Estado zerado. É função e não constante porque o objeto é atribuído direto
   * aos campos — uma constante compartilhada viraria estado global mutável entre
   * instâncias do componente.
   */
  private static emptyBroadcast(): BroadcastCallMetrics {
    return {
      total_calls: 0,
      total_refused: 0,
      total_answered_hangup: 0,
      total_answered_dtmf: 0,
      total_answered_listened: 0,
      total_broadcasts: 0,
    };
  }

  private static emptyAutomation(): AutomationCallMetrics {
    return {
      total_calls: 0,
      total_refused: 0,
      total_answered_hangup: 0,
      total_answered_dtmf: 0,
      total_answered_listened: 0,
      total_automations: 0,
      total_sequences: 0,
    };
  }

  constructor(
    private broadcastCallService: BroadcastCallService,
    private automationCallService: AutomationCallService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.loadBroadcasts();
    this.loadAutomations();

    // `queryParams` e não `snapshot`: quando só a query string muda, o Angular
    // reaproveita a instância do componente e o `ngOnInit` não roda de novo. Lendo
    // o snapshot uma vez, `?tab=` só valia em carga fria — quem já estivesse na
    // tela e abrisse um link de outra aba continuava vendo a aba antiga, com a URL
    // dizendo outra coisa. É o mesmo motivo pelo qual /#/calls-report assina.
    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        const tab: CallOrigin = params.tab === 'automation' || params.tab === 'broadcast'
          ? params.tab
          : 'all';

        // A primeira emissão sempre carrega, mesmo caindo na aba padrão; depois
        // dela, só recarrega quando a aba realmente muda — senão o clique na aba
        // dispararia dois requests (o do clique e o do queryParams que ele gera).
        if (this.initialTabApplied && tab === this.activeTab) { return; }

        this.initialTabApplied = true;
        this.activeTab = tab;
        this.load();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.metricsSub?.unsubscribe();
  }

  // ------------------------------------------------------------------- cards

  /**
   * As cinco faixas da aba ativa. O template desenha um bloco de cards só — os
   * rótulos e a classificação são os mesmos nas três abas, e duplicar o HTML
   * abriria a porta para elas divergirem visualmente.
   *
   * Em "Todos" a soma é feita AQUI, sobre os dois resultados que a tela já tem,
   * e não numa terceira consulta ao banco. É o que garante — por construção, não
   * por coincidência de dois SQLs — que Todos é exatamente Broadcasts +
   * Automações. Uma query própria poderia divergir das abas na primeira vez que
   * alguém mexesse num dos recortes, e o usuário veria três números que não
   * fecham sem ter como saber qual está errado.
   *
   * Somar não conta ninguém duas vezes: um lead é de broadcast OU de automação
   * (FKs mutuamente exclusivas em `call4u_leads`), nunca dos dois.
   */
  get metrics(): CallMetricsView {
    if (this.activeTab === 'broadcast') { return this.broadcastMetrics; }
    if (this.activeTab === 'automation') { return this.automationMetrics; }

    const b = this.broadcastMetrics;
    const a = this.automationMetrics;

    return {
      total_calls:             b.total_calls + a.total_calls,
      total_refused:           b.total_refused + a.total_refused,
      total_answered_hangup:   b.total_answered_hangup + a.total_answered_hangup,
      total_answered_dtmf:     b.total_answered_dtmf + a.total_answered_dtmf,
      total_answered_listened: b.total_answered_listened + a.total_answered_listened,
    };
  }

  /** O card de contagem de broadcasts aparece em "Todos" e na aba deles. */
  get showBroadcastCount(): boolean {
    return this.activeTab !== 'automation';
  }

  /** Idem para automações. */
  get showAutomationCount(): boolean {
    return this.activeTab !== 'broadcast';
  }

  /** Taxa de atendimento = tudo que não foi recusado/não atendido. */
  get answerRate(): string {
    const total = this.metrics.total_calls;
    if (!total) { return '0%'; }
    return ((total - this.metrics.total_refused) / total * 100).toFixed(1) + '%';
  }

  // -------------------------------------------------------------------- abas

  onTabChange(tab: CallOrigin): void {
    if (this.activeTab === tab) { return; }

    // Só navega: quem aplica a aba e recarrega é a assinatura de `queryParams`,
    // para o clique e o link compartilhado passarem pelo mesmo caminho.
    //
    // `tab: null` na aba padrão remove o parâmetro em vez de escrever `?tab=all`.
    // `replaceUrl` para a troca não empilhar histórico — o Voltar do navegador
    // tem que sair da tela, não desfazer cliques em aba.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab === 'all' ? null : tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // ---------------------------------------------------------- autocompletes

  loadBroadcasts(): void {
    this.subscriptions.push(
      this.broadcastCallService.getAll(1, 500).subscribe({
        next: result => {
          this.broadcasts = result.data;
          this.filteredBroadcasts$ = this.broadcastSearchCtrl.valueChanges.pipe(
            startWith(''),
            map(value => this.filterByName(this.broadcasts, value))
          );
        },
        error: () => {},
      })
    );
  }

  loadAutomations(): void {
    this.subscriptions.push(
      this.automationCallService.getAll().subscribe({
        next: result => {
          this.automations = result.data;
          this.filteredAutomations$ = this.automationSearchCtrl.valueChanges.pipe(
            startWith(''),
            map(value => this.filterByName(this.automations, value))
          );
        },
        error: () => {},
      })
    );
  }

  /** Filtro em memória compartilhado pelos dois autocompletes. */
  private filterByName<T extends { name: string }>(items: T[], search: any): T[] {
    if (!search || typeof search !== 'string') { return items; }
    const term = search.toLowerCase();
    return items.filter(item => item.name?.toLowerCase().includes(term));
  }

  /** `displayWith` do mat-autocomplete: serve às duas listas. */
  displayNameFn(item: { name: string }): string {
    return item?.name || '';
  }

  onBroadcastSelected(event: any): void {
    this.selectedBroadcastId = event.option.value?.id || '';
    this.load();
  }

  clearBroadcastFilter(): void {
    this.broadcastSearchCtrl.setValue('');
    this.selectedBroadcastId = '';
    this.load();
  }

  onAutomationSelected(event: any): void {
    this.selectedAutomationId = event.option.value?.id || '';
    this.load();
  }

  clearAutomationFilter(): void {
    this.automationSearchCtrl.setValue('');
    this.selectedAutomationId = '';
    this.load();
  }

  // ------------------------------------------------------------------ período

  getDateRange(): { start: string; end: string } {
    const end = new Date();
    const start = new Date();

    switch (this.dateFilter) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case 'custom':
        return {
          start: this.customStartDate ? this.formatDate(this.customStartDate) : this.formatDate(start),
          end: this.customEndDate ? this.formatDate(this.customEndDate) : this.formatDate(end),
        };
    }

    return { start: this.formatDate(start), end: this.formatDate(end) };
  }

  formatDate(d: Date): string {
    return d.toISOString().substring(0, 10);
  }

  // -------------------------------------------------------------------- carga

  /**
   * Motivo para NÃO consultar o período escolhido, ou `null` se ele é válido.
   *
   * Só o "Personalizado" chega aqui com algo inválido — os atalhos são sempre
   * corretos. As mesmas duas regras que o backend devolveria como 422.
   */
  periodError({ start, end }: { start: string; end: string }): string | null {
    if (end < start) {
      return 'A data final precisa ser igual ou posterior à data inicial';
    }

    const days = (Date.parse(end) - Date.parse(start)) / 86_400_000;
    if (days > LigacoesMetricasComponent.MAX_WINDOW_DAYS) {
      return `O período pode ter no máximo ${LigacoesMetricasComponent.MAX_WINDOW_DAYS} dias`;
    }

    return null;
  }

  load(): void {
    // Cancela o request anterior antes de abrir outro. Sem isso, trocar de aba
    // com uma resposta em voo deixava duas respostas competindo — e o `finalize`
    // da mais VELHA apagava o `loading` da mais nova.
    this.metricsSub?.unsubscribe();

    const range = this.getDateRange();
    const invalid = this.periodError(range);
    if (invalid) {
      this.loading = false;
      this.toastService.error(invalid);
      return;
    }

    const { start, end } = range;
    this.loading = true;
    const done = () => this.loading = false;

    // Um `subscribe` tipado por aba, e cada um grava SÓ no campo da sua origem.
    // A aba fica decidida no momento do request, não na chegada da resposta: ler
    // `this.activeTab` no `next` era um bug de verdade — quem clicasse "Todos" com
    // o request de Broadcasts em voo caía no ramo do consolidado e gravava
    // `result.broadcast`, `undefined` num payload que não tem essa chave.
    switch (this.activeTab) {
      case 'broadcast':
        this.metricsSub = this.broadcastCallService
          .getMetrics(start, end, this.selectedBroadcastId || undefined)
          .pipe(finalize(done))
          .subscribe({
            next: metrics => this.broadcastMetrics = metrics,
            error: () => this.toastService.error('Erro ao carregar métricas'),
          });
        break;

      case 'automation':
        this.metricsSub = this.automationCallService
          .getMetrics(start, end, this.selectedAutomationId || undefined)
          .pipe(finalize(done))
          .subscribe({
            next: metrics => this.automationMetrics = metrics,
            error: () => this.toastService.error('Erro ao carregar métricas'),
          });
        break;

      default:
        // Em "Todos" as duas consultas saem juntas e SEM filtro de id: o
        // consolidado não é recortável por um broadcast ou uma automação
        // específica — para isso existe a aba da origem.
        //
        // `catchError` por ramo, e não no forkJoin inteiro: sem isso o consolidado
        // é tudo-ou-nada, e uma falha do endpoint NOVO (deploy escalonado do
        // backend, 500 pontual) zeraria também as métricas de broadcast, que
        // respondem 200 e são o que já existia em produção.
        this.metricsSub = forkJoin({
          broadcast:  this.broadcastCallService.getMetrics(start, end)
            .pipe(catchError(err => this.skipFailedOrigin('broadcast', err))),
          automation: this.automationCallService.getMetrics(start, end)
            .pipe(catchError(err => this.skipFailedOrigin('automation', err))),
        })
          .pipe(finalize(done))
          .subscribe(({ broadcast, automation }) => {
            // Origem que falhou volta a zero em vez de manter o número da
            // consulta anterior — um card com dado velho ao lado de um card
            // novo é pior que um card zerado, porque não parece errado.
            this.broadcastMetrics  = broadcast  ?? LigacoesMetricasComponent.emptyBroadcast();
            this.automationMetrics = automation ?? LigacoesMetricasComponent.emptyAutomation();

            if (!broadcast && !automation) {
              this.toastService.error('Erro ao carregar métricas');
            } else if (!broadcast || !automation) {
              this.toastService.error('Erro ao carregar parte das métricas');
            }
          });
    }
  }

  /**
   * Engole a falha de UMA origem do consolidado para a outra continuar na tela.
   * O usuário vê o toast; o erro de verdade (status, corpo) fica no console, que
   * é onde dá para diagnosticar qual endpoint caiu.
   */
  private skipFailedOrigin(origin: Exclude<CallOrigin, 'all'>, err: unknown): Observable<null> {
    console.error(`[ligacoes-metricas] falha ao carregar as métricas de ${origin}`, err);
    return of(null);
  }

  applyFilter(): void {
    this.load();
  }
}
